/**
 * Client side of the activation-code gate — see relay/activation.ts for the
 * full design rationale (why sessions are per-device, why blocks
 * auto-recover on return to a venue, why the code rotates independently of
 * sessions).
 *
 * Gates the whole player-facing app, not just photo scan: see relay's
 * header comment on why. The admin route is a separate, PIN-gated flow that never
 * touches this module.
 */

import { writeBranding, type Branding } from './branding';
import { fetchWithTimeout } from './timedFetch';

const DEVICE_ID_KEY = 'mahjong-scorer:deviceId';
const SESSION_KEY = 'mahjong-scorer:session';

// Same pattern as src/vision/client.ts's RELAY_URL: in production
// VITE_VISION_RELAY_URL is the relay's bare origin (this module appends a
// path to it); in local dev, the Vite proxy forwards any /api/* path to the
// relay as-is, matched there by suffix — see relay/index.ts's router.
const RELAY_BASE = import.meta.env?.VITE_VISION_RELAY_URL ?? '/api';

export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

interface StoredSession {
  token: string;
  issuedAt: number;
}

const SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function readStoredSession(): StoredSession | undefined {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    if (typeof parsed.token !== 'string' || typeof parsed.issuedAt !== 'number') return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

/** Cheap client-side check so boot routing doesn't need a network round-trip just to decide which screen to show. The server is still the real source of truth — see checkSession. */
export function hasFreshCachedSession(): boolean {
  const stored = readStoredSession();
  return !!stored && Date.now() - stored.issuedAt <= SESSION_MAX_AGE_MS;
}

export function getSessionToken(): string | undefined {
  return readStoredSession()?.token;
}

function saveSession(token: string): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify({ token, issuedAt: Date.now() }));
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

// ---------------------------------------------------------------------------
// Geolocation — mandatory, no fallback (see the design discussion: IP-based
// location is too unreliable, especially on cellular, to trust for this).
// ---------------------------------------------------------------------------

/** How long the browser gets to say whether location is allowed before the app simply asks for it. */
export const GEO_PERMISSION_CHECK_MS = 2000;

export type GeoPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/**
 * Distinguishes "never asked" from "browser has permanently blocked this
 * site" — Safari doesn't support querying geolocation permission, so this
 * falls back to 'prompt' there (meaning: just try, see what happens) rather
 * than claiming certainty it doesn't have.
 */
export async function queryGeoPermission(): Promise<GeoPermissionState> {
  if (!('geolocation' in navigator)) return 'unsupported';
  if (!('permissions' in navigator)) return 'prompt';
  // Some in-app browsers (a QR scanner, a chat app's viewer) never answer this question. It has a deadline: no answer in two seconds
  // is treated like Safari's "can't tell" — just ask for the location and see what happens — so Continue can never sit there silent.
  const noAnswer = new Promise<GeoPermissionState>((resolve) => setTimeout(() => resolve('prompt'), GEO_PERMISSION_CHECK_MS));
  const asked = (async (): Promise<GeoPermissionState> => {
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      return status.state as GeoPermissionState;
    } catch {
      return 'prompt';
    }
  })();
  return Promise.race([asked, noAnswer]);
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export type GeoResult = { ok: true; coords: Coordinates } | { ok: false; reason: 'denied' | 'unavailable' };

/** At the start of a game the location only feeds the check-in, so it is not worth a long wait. */
export const GAME_START_LOCATION_TIMEOUT_MS = 6000;

export function readLocation(timeoutMs = 15000): Promise<GeoResult> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve({ ok: false, reason: 'unavailable' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ ok: true, coords: { latitude: pos.coords.latitude, longitude: pos.coords.longitude } }),
      (err) => resolve({ ok: false, reason: err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable' }),
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60000 },
    );
  });
}

// ---------------------------------------------------------------------------
// Activate / verify
// ---------------------------------------------------------------------------

export type ActivateResult = { ok: true } | { ok: false; error: string };

/** A first activation from a phone far from the data can be slow; past this it is told so instead of waiting on. */
export const ACTIVATE_TIMEOUT_MS = 25_000;
export const UNREACHABLE_MESSAGE = 'The server did not answer. Check your connection and tap Continue to try again.';

/** The initial handshake: QR-scanned code + mandatory GPS reading → a cached session. */
export async function activate(code: string, coords: Coordinates): Promise<ActivateResult> {
  let res: Response;
  try {
    res = await fetchWithTimeout(
      `${RELAY_BASE}/activate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, deviceId: getDeviceId(), ...coords }),
      },
      ACTIVATE_TIMEOUT_MS,
    );
  } catch {
    // A failed connection or no answer in time: say so (the screen used to sit on "Checking Code…" for ever, because this threw
    // out of a call nobody was waiting on).
    return { ok: false, error: UNREACHABLE_MESSAGE };
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return { ok: false, error: body.error ?? 'Activation failed.' };
  }

  let body: { token: string; branding?: Branding };
  try {
    body = (await res.json()) as { token: string; branding?: Branding };
  } catch {
    return { ok: false, error: UNREACHABLE_MESSAGE };
  }
  saveSession(body.token);
  // Whatever the venue/global default resolved to server-side becomes this
  // device's own cached branding — see src/ui/branding.ts's doc comment for
  // why this device otherwise never sees anything but the generic default.
  if (body.branding) writeBranding(body.branding);
  return { ok: true };
}

/**
 * The periodic recheck — fired at the start of each new game, not on every request. Confirms the cached session is still valid
 * server-side and refreshes its recorded location. Clears the cached session when the server says it is no longer valid, so the next
 * boot routes back to the activation screen.
 *
 * `tableId`, when passed, is stamped onto the session record so the admin session list can show which table a device is on — this is
 * the only game data a normal (Fun/Serious) game ever sends the relay; player names never leave the device. The one exception is
 * Leaderboard mode, where the registered nicknames are sent on purpose (see leaderboardClient.ts).
 */
export type SessionCheck = 'valid' | 'invalid' | 'unreachable';

/** How long the start-of-game check waits for the server before the game goes ahead without it. */
export const SESSION_CHECK_TIMEOUT_MS = 8_000;

/**
 * Three answers, not two: `invalid` is the server saying no (the session is gone or the code has rotated — activate again);
 * `unreachable` is no answer at all (no connection, a stalled request, a server in trouble) — which says nothing about the session.
 * It used to throw, so a dropped connection left the Start button doing nothing, with no message.
 */
export async function checkSession(coords?: Coordinates, tableId?: string): Promise<SessionCheck> {
  const token = getSessionToken();
  if (!token) return 'invalid';

  let res: Response;
  try {
    res = await fetchWithTimeout(
      `${RELAY_BASE}/verify`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...coords, tableId }),
      },
      SESSION_CHECK_TIMEOUT_MS,
    );
  } catch {
    return 'unreachable';
  }

  // A server in trouble (5xx) or asking to slow down (429) has not said the session is bad.
  if (res.status >= 500 || res.status === 429) return 'unreachable';
  if (!res.ok) {
    clearSession();
    return 'invalid';
  }
  let body: { valid: boolean; branding?: Branding };
  try {
    body = (await res.json()) as { valid: boolean; branding?: Branding };
  } catch {
    return 'unreachable';
  }
  if (!body.valid) {
    clearSession();
    return 'invalid';
  }
  if (body.branding) writeBranding(body.branding);
  return 'valid';
}

/**
 * The admin-configured photo-scan attempt limit per round — a cost control
 * (each recognition call is a real paid API request), not a security check.
 * Falls back to the same conservative default the relay itself uses when
 * unreachable, so a network blip never silently uncaps spending.
 */
export async function getMaxScanAttempts(): Promise<number> {
  const FALLBACK = 1;
  const token = getSessionToken();
  if (!token) return FALLBACK;

  try {
    const res = await fetch(`${RELAY_BASE}/config`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return FALLBACK;
    const body = (await res.json()) as { maxScanAttempts?: number };
    return typeof body.maxScanAttempts === 'number' ? body.maxScanAttempts : FALLBACK;
  } catch {
    return FALLBACK;
  }
}
